// Zona en la que el backend serializa sus fechas (BookingV2 `config/app.php`).
const APP_TZ = 'America/Monterrey'

// `sv-SE` formatea como `YYYY-MM-DD HH:mm:ss`, que es ISO salvo por la T.
const APP_TZ_FORMAT = new Intl.DateTimeFormat('sv-SE', {
	timeZone: APP_TZ,
	year: 'numeric',
	month: '2-digit',
	day: '2-digit',
	hour: '2-digit',
	minute: '2-digit',
	second: '2-digit',
	hour12: false,
})

const APP_TZ_DATE_FORMAT = new Intl.DateTimeFormat('sv-SE', {
	timeZone: APP_TZ,
	year: 'numeric',
	month: '2-digit',
	day: '2-digit',
})

/**
 * "Hoy" en `Y-m-d`, en la zona del backend — no la del navegador. Un filtro
 * como `from_date=hoy` tiene que coincidir con lo que el backend entiende por
 * "hoy": calcularlo con la hora local del navegador movería la fecha cerca de
 * medianoche para cualquiera que no esté en México (mismo problema que este
 * archivo documenta para las fechas que SÍ vienen del backend).
 */
export function todayInAppTz(): string {
	return APP_TZ_DATE_FORMAT.format(new Date())
}

const HAS_ZONE = /(Z|[+-]\d{2}:?\d{2})$/

/**
 * Normaliza una fecha del backend al WALL CLOCK literal: `YYYY-MM-DDTHH:mm:ss`,
 * sin zona, listo para que `new Date()` / `moment()` lo lean como hora local y
 * lo vuelvan a pintar con los MISMOS dígitos en cualquier navegador.
 *
 * Hace falta porque casi ninguna fecha de viaje es un instante, aunque lo
 * parezca. Un vuelo sale a las 10:30 hora del aeropuerto y un check-in es el
 * día 18 en el hotel: eso no cambia porque el viajero abra la app en Los
 * Ángeles. El backend, en cambio, entrega tres formas distintas:
 *
 *   1. `2026-07-10T10:30:00`            oferta viva de Sabre — naive a propósito
 *      (`SabreFlightOffer::stripTimezoneFromLegs`). Los dígitos YA son el wall clock.
 *   2. `2026-07-10T16:30:00.000000Z`    el MISMO 10:30 después de pasar por la BD:
 *      `segments.departure_datetime` / `requests.start_date` son `timestamp` sin
 *      zona y Laravel los serializa como si fueran hora de la app. La `Z` miente.
 *   3. `2026-07-10`                     fecha sola (seat map, params de búsqueda),
 *      que `new Date()` leería como medianoche UTC.
 *
 * Sin normalizar, (2) corre la hora en cuanto el navegador no está en México
 * (16:30Z → 09:30 en America/Los_Angeles) y de noche se lleva el día entero
 * (`14 abr 18:28` se guarda `2026-04-15T00:28:00Z` → 15 de abril al este de
 * México); y (3) pinta el día anterior en TODA América.
 *
 * ponytail: revierte (2) asumiendo que el backend serializa desde APP_TZ, que es
 * lo único que la respuesta permite deducir hoy. El fix de raíz es que el backend
 * deje de fingir que la hora local de un aeropuerto es un instante — mandar el
 * offset o el IATA y formatear contra la zona real del aeropuerto. Levantado en
 * **BKG-433**; cuando eso se implemente, este helper se borra.
 */
export function toWallClock(value: string): string {
	if (!value) return value
	// Fecha sola: los dígitos ya son el wall clock; la hora explícita evita que
	// `new Date()` la lea como medianoche UTC.
	if (!value.includes('T') && !value.includes(' ')) return `${value}T00:00:00`
	// Naive: ya viene tal cual.
	if (!HAS_ZONE.test(value)) return value
	// Falso instante: devolverlo a la zona de la app recupera los dígitos originales.
	return APP_TZ_FORMAT.format(new Date(value)).replace(' ', 'T')
}
